import {
  AssetClassCode,
  AssetSector,
  AssetSubClassCode,
  AssetSubClassMap,
  Class,
} from '@api/api/entity-constants';
import {
  AssetLogoComputedProps,
  AssetLogoVariant,
  SECTOR_TO_ICON,
  SUB_CLASS_TO_ICON,
} from '@api/utils/assetLogo';
import type { ChartEntity } from '@chart/types/chart-entity.types';

const PRIMARY_METHOD_PRICE = 'price';

const getLogoVariant = (entity: ChartEntity): AssetLogoVariant | undefined => {
  if (entity.class === Class.ClassStock) {
    return 'StockInitial';
  }
  if (entity.asset_class === AssetClassCode.Equity) {
    return 'EquitySector';
  }
  if (entity.asset_class === AssetClassCode.Fi) {
    return 'FinancialInstrument';
  }
  if (entity.sub_class === AssetSubClassCode.FxCrypto) {
    return 'Crypto';
  }
  if (entity.asset_class === AssetClassCode.Fx) {
    return 'FxCurrency';
  }
  if (entity.asset_class === AssetClassCode.Currency) {
    return 'Default';
  }
  return undefined;
};

export const getChartEntityLogoDisplay = (
  entity: ChartEntity
): AssetLogoComputedProps => {
  const variant = getLogoVariant(entity);
  const isNotStockEntity =
    entity.class !== Class.ClassStock &&
    entity.class !== Class.ClassUnspecified;
  const isFiEntity = entity.class === Class.ClassFi;
  const sectorCode = entity.gics?.sector?.code ?? '';
  const iconName =
    (sectorCode && SECTOR_TO_ICON[sectorCode]) ||
    SECTOR_TO_ICON[AssetSector.Other];
  const subClassIcon =
    SUB_CLASS_TO_ICON[AssetSubClassMap[entity.sub_class]] ?? 'ChartLine';
  return { variant, isNotStockEntity, isFiEntity, iconName, subClassIcon };
};

const isPriceSnake = (snake: string) => /\.price$/.test(snake);

export const isSinglePrice = (entity: ChartEntity): boolean => {
  if (!entity || typeof entity !== 'object') {
    return false;
  }
  return (
    entity.primary_method !== PRIMARY_METHOD_PRICE ||
    entity.sub_class === AssetSubClassCode.FxCrypto ||
    !isPriceSnake(entity.default_snake)
  );
};

export const isFX = (entity: ChartEntity): boolean =>
  [AssetSubClassCode.MajorCross, AssetSubClassCode.GenericCross].includes(
    entity.sub_class
  );

export const isCommodity = (entity: ChartEntity): boolean =>
  [
    AssetSubClassCode.FutureEnergy,
    AssetSubClassCode.FutureFood,
    AssetSubClassCode.FutureGrains,
    AssetSubClassCode.FutureMetals,
    AssetSubClassCode.FutureSofts,
  ].includes(entity.sub_class);
